<template>
  <div class="extract-wrapper">
    <v-tabs
      v-model="activeTab"
      color="transparent"
      slider-color="primary"
      class="extract-tabs"
      fixed-tabs
    >
      <v-tab
        v-for="t in tabs"
        :key="t.key"
        :href="`#tab-${t.key}`"
        class="extract-tab"
      >
        {{ t.label }}
      </v-tab>

      <v-tab-item
        v-for="t in tabs"
        :key="t.key"
        :value="`tab-${t.key}`"
      >
        <div v-resize="onResize" class="extract-list-container">
          <!-- Loading -->
          <div v-if="isLoading" class="extract-state">
            <v-progress-circular indeterminate color="primary" size="40" />
            <span class="extract-state__text">Carregando transações...</span>
          </div>

          <!-- Empty -->
          <div v-else-if="!t.items.length" class="extract-state">
            <v-icon size="56" color="grey lighten-1">receipt_long</v-icon>
            <span class="extract-state__text">Nenhuma transação encontrada neste período.</span>
          </div>

          <!-- Transactions grouped by date -->
          <template v-else>
            <div
              v-for="group in groups(t.items)"
              :key="group.date"
              class="extract-group"
            >
              <div class="extract-group__header">{{ group.label }}</div>

              <v-list two-line class="extract-list">
                <v-list-tile
                  v-for="item in group.items"
                  :key="item.id"
                  class="extract-row"
                  avatar
                >
                  <v-list-tile-avatar
                    :class="['tx-avatar', item.positive ? 'credit' : 'debit']"
                  >
                    <v-icon>{{ getIcon(item) }}</v-icon>
                  </v-list-tile-avatar>

                  <v-list-tile-content>
                    <v-list-tile-title class="extract-row__title">
                      {{ $t(`kind.${item.kind}`) }}
                    </v-list-tile-title>
                    <v-list-tile-sub-title class="extract-row__subtitle">
                      Nº {{ item.id }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>

                  <v-list-tile-action class="extract-row__action">
                    <span :class="['extract-row__amount', getColor(item)]">
                      {{ item.positive ? '+' : '-' }} {{ $n(item.amount, "currency") }}
                    </span>
                    <v-chip
                      :class="['extract-chip', getStatusClass(item)]"
                      small
                      text-color="white"
                    >
                      {{ getTransactionStatus(item) }}
                    </v-chip>
                  </v-list-tile-action>
                </v-list-tile>
              </v-list>
            </div>

            <!-- Footer: pagination + rows per page -->
            <div class="extract-footer">
              <v-select
                :items="limitOptions"
                :value="limit"
                label="Por página"
                class="extract-footer__limit"
                hide-details
                @change="changeLimit"
              />
              <v-pagination
                v-if="pageCount(t.total) > 1"
                :value="page"
                :length="pageCount(t.total)"
                :total-visible="7"
                color="primary"
                @input="changePage"
              />
              <span class="extract-footer__count">{{ t.total }} transações</span>
            </div>
          </template>
        </div>
      </v-tab-item>
    </v-tabs>
  </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

export default {
  created() {
    this.$store.dispatch("wallet/getWallet");
  },
  computed: {
    ...mapGetters({
      filterParams: "wallet/filterParams",
      extract: "wallet/all",
      totalResources: "wallet/totalResources",
      receiveds: "wallet/received",
      receivedsTotal: "wallet/receivedsTotal",
      sendeds: "wallet/sended",
      sendedsTotal: "wallet/sendedsTotal",
      limitOptions: "wallet/limitOptions",
      page: "wallet/page",
      limit: "wallet/limit",
      isLoading: "wallet/loadingData",
    }),
    tabs() {
      return [
        {
          key: "all", label: "Todos", items: this.extract, total: this.totalResources,
        },
        {
          key: "received", label: "Recebidos", items: this.receiveds, total: this.receivedsTotal,
        },
        {
          key: "sended", label: "Enviados", items: this.sendeds, total: this.sendedsTotal,
        },
      ];
    },
  },
  methods: {
    ...mapActions({
      setCurrentPage: "wallet/setCurrentPage",
      setLimit: "wallet/setLimit",
    }),
    // Agrupa transações por data preservando a ordem recebida do backend
    groups(items) {
      const today = moment().format("DD/MM/YYYY");
      const yesterday = moment().subtract(1, "day").format("DD/MM/YYYY");
      const map = [];
      const index = {};

      items.forEach((item) => {
        const date = item.created_at;
        if (index[date] === undefined) {
          index[date] = map.length;
          let label = date;
          if (date === today) label = "Hoje";
          else if (date === yesterday) label = "Ontem";
          map.push({ date, label, items: [] });
        }
        map[index[date]].items.push(item);
      });

      return map;
    },
    pageCount(total) {
      return Math.max(1, Math.ceil((total || 0) / (this.limit || 10)));
    },
    refetch() {
      this.$store.dispatch(
        "wallet/getExtract",
        this.$store.getters["wallet/filterParams"],
      );
    },
    changePage(newPage) {
      if (newPage === this.page) return;
      this.setCurrentPage(newPage);
      this.refetch();
    },
    changeLimit(newLimit) {
      if (newLimit === this.limit) return;
      this.setLimit(newLimit);
      this.refetch();
    },
    getIcon(item) {
      const icons = {
        digital_recharge: "account_balance_wallet",
        digital_bill_deposit: "south_west",
        digital_liquidation: "check_circle",
        digital_payment: "receipt_long",
        bill_payment: "receipt_long",
        digital_withdraw: "account_balance",
        digital_transfer_sended: "north_east",
        digital_transfer_received: "south_west",
        digital_swipe_transfer_incremment: "south_west",
        digital_swipe_transfer_decremment: "north_east",
        digital_system_incremment: "tune",
        digital_system_decremment: "tune",
        refound: "undo",
      };
      return icons[item.kind] || (item.positive ? "south_west" : "north_east");
    },
    getColor(item) {
      return item.positive ? "positive" : "negative";
    },
    getTransactionStatus(item) {
      // Para transações de saque - usar status do WalletWithdraw quando disponível
      if (item.kind === "digital_withdraw") {
        if (item.withdraw_status_display) {
          return item.withdraw_status_display;
        }
        // Fallback para o sistema antigo
        if (item.withdraw_approved === false) {
          return "Aguardando Aprovação";
        } if (item.withdraw_approved === true) {
          return "Aprovado";
        }
      }

      // Para estornos de saque
      if (item.kind === "refound" && item.withdraw_status_display) {
        return "Estornado";
      }

      // Para outras transações
      switch (item.kind) {
        case "digital_liquidation":
          return "Liquidado";
        case "digital_transfer_received":
          return "Recebido";
        case "digital_transfer_sended":
          return "Enviado";
        case "digital_payment":
          return "Pago";
        case "refound":
          return "Estornado";
        case "bill_payment":
          return "Pago";
        default:
          return "Processado";
      }
    },
    getStatusClass(item) {
      // Para transações de saque - usar status do WalletWithdraw quando disponível
      if (item.kind === "digital_withdraw") {
        if (item.withdraw_status) {
          switch (item.withdraw_status) {
            case "waiting":
              return "waiting";
            case "approved":
              return "approved";
            case "refused":
              return "cancelled";
            case "paid":
              return "completed";
            default:
              return "scheduled";
          }
        }
        // Fallback para o sistema antigo
        if (item.withdraw_approved === false) {
          return "waiting";
        } if (item.withdraw_approved === true) {
          return "approved";
        }
      }

      // Para estornos de saque
      if (item.kind === "refound" && item.withdraw_status) {
        return "cancelled";
      }

      // Para outras transações
      switch (item.kind) {
        case "digital_liquidation":
        case "digital_transfer_received":
        case "digital_payment":
        case "bill_payment":
          return "completed";
        case "digital_transfer_sended":
          return "processing";
        case "refound":
          return "cancelled";
        default:
          return "scheduled";
      }
    },
    onResize() {
      this.isMobile = window.innerWidth < 769;
    },
  },
  data() {
    return {
      activeTab: "tab-all",
      isMobile: false,
    };
  },
};
</script>

<style scoped>
.extract-wrapper {
  margin-top: 8px;
}

.extract-tabs >>> .v-tabs__bar {
  background: transparent !important;
}

.extract-tab {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: #6b7280 !important;
}

.extract-list-container {
  padding: 8px 4px 16px;
  min-height: 200px;
}

/* Date group */
.extract-group {
  margin-bottom: 4px;
}

.extract-group__header {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #9aa0a6;
  padding: 14px 16px 6px;
}

.extract-list {
  background: transparent !important;
  padding: 0;
}

/* Row */
.extract-row {
  border-radius: 12px;
  margin: 2px 4px;
  transition: background-color 0.15s ease;
}

.extract-row:hover {
  background-color: #f5f7fa;
}

.extract-row__title {
  font-weight: 600;
  color: #1f2933;
  font-size: 15px;
}

.extract-row__subtitle {
  color: #9aa0a6 !important;
  font-size: 12.5px;
}

.extract-row__action {
  align-items: flex-end !important;
  justify-content: center;
}

.extract-row__amount {
  font-weight: 700;
  font-size: 15px;
  white-space: nowrap;
}

/* Avatar tints */
.tx-avatar {
  border-radius: 50%;
}
.tx-avatar.credit {
  background-color: #e6f7ed;
}
.tx-avatar.credit .v-icon {
  color: #10b83a !important;
}
.tx-avatar.debit {
  background-color: #fdecec;
}
.tx-avatar.debit .v-icon {
  color: #fa2a2a !important;
}

.positive {
  color: #10b83a;
}
.negative {
  color: #fa2a2a;
}

/* Status chip */
.extract-chip {
  margin: 4px 0 0 !important;
  height: 20px;
  font-size: 11px;
  font-weight: 600;
}
.extract-chip.cancelled {
  background-color: #b71c1c !important;
}
.extract-chip.approved {
  background-color: rgb(28, 139, 35) !important;
}
.extract-chip.completed {
  background-color: rgb(28, 139, 3) !important;
}
.extract-chip.waiting {
  background-color: #ef6c00 !important;
}
.extract-chip.scheduled {
  background-color: rgb(51, 197, 202) !important;
}
.extract-chip.processing {
  background-color: rgb(34, 93, 180) !important;
}

/* States */
.extract-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 56px 16px;
}
.extract-state__text {
  color: #9aa0a6;
  font-size: 14px;
}

/* Footer */
.extract-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 12px 16px 4px;
  border-top: 1px solid #eef0f2;
  margin-top: 8px;
}
.extract-footer__limit {
  max-width: 120px;
}
.extract-footer__count {
  color: #9aa0a6;
  font-size: 12.5px;
}

@media screen and (max-width: 600px) {
  .extract-row__amount {
    font-size: 14px;
  }
  .extract-footer {
    justify-content: space-between;
  }
}
</style>
